import { CategoryBrowseTab, ImageCategory } from './types'
import type { CategoryTab, CategoryTabOption, Image, ImageCategoryTabOption, SlideCounter } from './types'
import { galleryCategoryTabEventLabels } from '../../../../services/analytics-service/labels'

const CATEGORY_TAB_EVENT_LABELS: Record<CategoryTab, string> = {
    [CategoryBrowseTab.ALL]: galleryCategoryTabEventLabels.ALL,
    [CategoryBrowseTab.VIDEO]: galleryCategoryTabEventLabels.VIDEO,
    [ImageCategory.EXTERIOR_SHOTS]: galleryCategoryTabEventLabels.EXTERIOR_SHOTS,
    [ImageCategory.OUTDOOR_SURROUNDING]: galleryCategoryTabEventLabels.OUTDOOR_SURROUNDING,
    [ImageCategory.FEATURES]: galleryCategoryTabEventLabels.FEATURES,
    [ImageCategory.GROUND_PLAN]: galleryCategoryTabEventLabels.MISCELLANEOUS,
    [ImageCategory.DOCUMENTS]: galleryCategoryTabEventLabels.MISCELLANEOUS,
    [ImageCategory.LIVING_ROOM]: galleryCategoryTabEventLabels.LIVING_ROOM,
    [ImageCategory.BEDROOM]: galleryCategoryTabEventLabels.BEDROOM,
    [ImageCategory.BATHROOM]: galleryCategoryTabEventLabels.BATHROOM,
    [ImageCategory.KITCHEN]: galleryCategoryTabEventLabels.KITCHEN,
    [ImageCategory.SWIMMING_POOL]: galleryCategoryTabEventLabels.SWIMMING_POOL,
    [ImageCategory.MISCELLANEOUS]: galleryCategoryTabEventLabels.MISCELLANEOUS,
}

export const getCategoryTabEventLabel = (tab: CategoryTab): string => CATEGORY_TAB_EVENT_LABELS[tab]

const CATEGORY_TAB_OPTIONS: ImageCategoryTabOption[] = [
    { key: ImageCategory.EXTERIOR_SHOTS, translationKey: 'gallery.categoryFacade' },
    { key: ImageCategory.SWIMMING_POOL, translationKey: 'gallery.categorySwimmingPool' },
    { key: ImageCategory.LIVING_ROOM, translationKey: 'gallery.categoryLivingRoom' },
    { key: ImageCategory.BEDROOM, translationKey: 'gallery.categoryBedroom' },
    { key: ImageCategory.KITCHEN, translationKey: 'gallery.categoryKitchen' },
    { key: ImageCategory.BATHROOM, translationKey: 'gallery.categoryBathroom' },
    { key: ImageCategory.OUTDOOR_SURROUNDING, translationKey: 'gallery.categoryOutdoor' },
    { key: ImageCategory.FEATURES, translationKey: 'gallery.categoryFurnishing' },
    { key: ImageCategory.MISCELLANEOUS, translationKey: 'gallery.categoryOthers' },
]

const OTHERS_CATEGORY_IDS = new Set<ImageCategory>([
    ImageCategory.GROUND_PLAN,
    ImageCategory.DOCUMENTS,
    ImageCategory.MISCELLANEOUS,
])

const normalizeCategory = (category?: ImageCategory): ImageCategory => {
    if (category === undefined || OTHERS_CATEGORY_IDS.has(category)) {
        return ImageCategory.MISCELLANEOUS
    }
    return category
}

export const imageMatchesCategoryTab = (image: Image, tab: CategoryTab): boolean => {
    if (tab === CategoryBrowseTab.ALL || tab === CategoryBrowseTab.VIDEO) {
        return false
    }
    return normalizeCategory(image.category) === tab
}

export const getImageCategoryTab = (image: Image): ImageCategory => normalizeCategory(image.category)

export const getImagesForCategoryTab = (
    images: Image[],
    video: Image | undefined,
    tab: CategoryTab,
): { images: Image[]; video?: Image } => {
    if (tab === CategoryBrowseTab.ALL) {
        return { images, video }
    }
    if (tab === CategoryBrowseTab.VIDEO) {
        return { images: [], video }
    }
    return { images: images.filter(image => imageMatchesCategoryTab(image, tab)), video: undefined }
}

export type ImagesByCategory = Map<ImageCategory, Image[]>

export const groupImagesByCategory = (images: Image[]): ImagesByCategory => {
    const imagesByCategory: ImagesByCategory = new Map()

    images.forEach(image => {
        const category = normalizeCategory(image.category)
        const categoryImages = imagesByCategory.get(category)

        if (categoryImages) {
            categoryImages.push(image)
        } else {
            imagesByCategory.set(category, [image])
        }
    })

    return imagesByCategory
}

export const getCategoryOrderedImages = (imagesByCategory: ImagesByCategory): Image[] =>
    CATEGORY_TAB_OPTIONS.flatMap(option => imagesByCategory.get(option.key) ?? [])

export const getAvailableCategoryTabs = (
    imagesByCategory: ImagesByCategory,
    video: Image | undefined,
): CategoryTabOption[] => {
    const tabs: CategoryTabOption[] = [{ key: CategoryBrowseTab.ALL, translationKey: 'gallery.categoryAll' }]

    if (video) {
        tabs.push({ key: CategoryBrowseTab.VIDEO, translationKey: 'gallery.videos' })
    }

    CATEGORY_TAB_OPTIONS.forEach(option => {
        if (imagesByCategory.has(option.key)) {
            tabs.push(option)
        }
    })

    return tabs
}

export const getCategoryCounters = (imagesByCategory: ImagesByCategory): Map<Image, SlideCounter> => {
    const counters = new Map<Image, SlideCounter>()

    imagesByCategory.forEach(categoryImages =>
        categoryImages.forEach((image, index) =>
            counters.set(image, { current: index + 1, total: categoryImages.length }),
        ),
    )

    return counters
}
